import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { deleteSale } from "@/Api/sales";
import type { TableDataProps as SalesListTableDataProps } from "@/pages/SalesList";
import type { TableDataProps as MarketplaceTableDataProps } from "@/pages/Marketplace";
import { Mask } from "@/utils/mask";

type CancelableSale =
  | SalesListTableDataProps
  | MarketplaceTableDataProps;

export type CancelSaleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  sale: CancelableSale | null;
  onSaleCancelled: () => void | Promise<void>;
};

export const CancelSaleModal = ({
  isOpen,
  onClose,
  sale,
  onSaleCancelled,
}: CancelSaleModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = async () => {
    if (!sale) return;

    setIsLoading(true);
    try {
      const { ok } = await deleteSale(sale.uuid);

      if (ok) {
        toast.success("Venda cancelada com sucesso!");
        await onSaleCancelled();
        onClose();
      }
    } catch (error) {
      console.error("Erro ao cancelar venda:", error);
      toast.error("Erro ao cancelar venda");
    } finally {
      setIsLoading(false);
    }
  };

  if (!sale) return null;

  const saleData = sale as any;
  const clientName = saleData?.client?.name ?? "—";
  const totalValue = Mask.formatCurrency(
    parseFloat(String(saleData?.total ?? "0"))
  );
  const sellerName =
    saleData?.seller?.user?.name ??
    saleData?.seller?.name ??
    saleData?.created_by?.name ??
    "—";
  const saleDate = saleData?.created_at
    ? new Date(saleData.created_at).toLocaleDateString("pt-BR")
    : "—";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <X className="size-5" />
            Cancelar Venda
          </DialogTitle>
          <DialogDescription className="text-left space-y-2">
            <div className="font-medium text-gray-900 dark:text-gray-100">
              Tem certeza que deseja cancelar esta venda?
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md space-y-1">
              <div><strong>ID:</strong> #{sale.id}</div>
              <div><strong>Cliente:</strong> {clientName}</div>
              <div><strong>Valor:</strong> {totalValue}</div>
              <div><strong>Vendedor:</strong> {sellerName}</div>
              <div><strong>Data:</strong> {saleDate}</div>
            </div>
            <div className="text-red-600 font-medium">
              ⚠️ Esta ação não pode ser desfeita!
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            Não, manter venda
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            {isLoading ? (
              <>
                <Loader className="animate-spin size-4 mr-2" />
                Cancelando...
              </>
            ) : (
              <>
                <X className="size-4 mr-2" />
                Sim, cancelar venda
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
